import { buildRuntimeRequestUrl } from '@core/veyopRuntime';
import { t } from './i18n';
import type {
  DesignTemplate,
  DesignTemplateRuntimeConfig,
  DesignTemplateSummary,
  DesignerTemplateConfig,
  PublicDesignTemplate,
  SaveDesignTemplateInput,
  TemplateBulkAction,
  TemplateListParams,
  TemplateListResult,
  TemplateTag,
} from './types';

declare global {
  interface Window {
    VeyopDesignTemplates?: DesignTemplateRuntimeConfig;
  }
}

interface ApiEnvelope<T> {
  error?: number;
  msg?: string;
  data?: T;
}

type PortableVeyop = {
  http?: { request<T>(path: string, options?: RequestInit): Promise<T> };
  runtime?: {
    apiUrl?: string;
    baseUrl?: string;
    requestHeaders?: Record<string, string>;
  };
};

function portableVeyop(): PortableVeyop | undefined {
  return (window as Window & { Veyop?: PortableVeyop }).Veyop;
}

function hostRequestHeaders(): Record<string, string> {
  const configured = (window as Window & {
    VeyopHostConfig?: { requestHeaders?: Record<string, string> };
  }).VeyopHostConfig?.requestHeaders;
  return {
    ...(configured ?? {}),
    ...(portableVeyop()?.runtime?.requestHeaders ?? {}),
  };
}

function trimSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function derivePluginBaseUrl(): string {
  const runtimeBase = window.VeyopDesignTemplates?.pluginBaseUrl;
  if (runtimeBase) return trimSlash(runtimeBase);

  const script = Array.from(document.scripts)
    .map((item) => item.src)
    .find((src) => src.includes('/plugins/design-templates/dist/'));
  const marker = '/plugins/design-templates/';
  const idx = script ? script.indexOf(marker) : -1;
  if (script && idx >= 0) return trimSlash(script.slice(0, idx));

  return window.location.origin;
}

export function deriveRestUrl(pluginBaseUrl = ''): string {
  const runtime = window.VeyopDesignTemplates?.restUrl;
  if (runtime) return trimSlash(runtime);
  const apiUrl = portableVeyop()?.runtime?.apiUrl;
  return apiUrl ? `${trimSlash(apiUrl)}/design-templates` : `${window.location.origin}/api/v1/design-templates`;
}

function runtime(pluginBaseUrl = ''): DesignTemplateRuntimeConfig {
  const current = window.VeyopDesignTemplates;
  return {
    restUrl: deriveRestUrl(current?.restUrl || pluginBaseUrl),
    pluginBaseUrl: current?.pluginBaseUrl || derivePluginBaseUrl(),
  };
}

/**
 * OpenCart / PrestaShop pages that load an addon bundle without the core runtime
 * (e.g. the product editor) still publish VeyopHostConfig: route through it.
 */
function nativeHostUrl(path: string, params: Record<string, string>): string | null {
  const host = (window as Window & {
    VeyopHostConfig?: { apiUrl?: string; baseUrl?: string; platform?: string };
  }).VeyopHostConfig;
  if (window.VeyopDesignTemplates?.restUrl || !host?.apiUrl || !host.platform || host.platform === 'wordpress') return null;
  const query = new URLSearchParams(params).toString();
  return buildRuntimeRequestUrl(
    { apiUrl: host.apiUrl, baseUrl: host.baseUrl ?? '', platform: host.platform as 'opencart' | 'prestashop' },
    `/design-templates${path}${query ? `?${query}` : ''}`,
  );
}

function buildUrl(path = '', params: Record<string, string> = {}, pluginBaseUrl = ''): string {
  const native = nativeHostUrl(path, params);
  if (native) return native;
  const base = runtime(pluginBaseUrl).restUrl;
  const url = new URL(`${trimSlash(base)}${path}`, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== '') url.searchParams.set(key, value);
  });
  return url.toString();
}

async function request<T>(path: string, options: {
  method?: string;
  body?: unknown;
  params?: Record<string, string>;
  pluginBaseUrl?: string;
} = {}): Promise<T> {
  const method = options.method || 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...hostRequestHeaders(),
  };

  const init: RequestInit = {
    method,
    headers,
    credentials: 'same-origin',
  };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(options.body);
  }

  const portable = portableVeyop()?.http;
  if (portable) {
    const query = new URLSearchParams(options.params ?? {}).toString();
    return portable.request<T>(`/design-templates${path}${query ? `?${query}` : ''}`, init);
  }

  const response = await fetch(buildUrl(path, options.params, options.pluginBaseUrl), init);
  const json = await response.json().catch(() => ({})) as ApiEnvelope<T>;
  if (!response.ok || Number(json.error ?? 0) !== 0) {
    throw new Error(json.msg || response.statusText || t('Request failed'));
  }
  return json.data as T;
}

function listParams(params: object): Record<string, string> {
  const out: Record<string, string> = {};
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') out[key] = String(value);
  });
  return out;
}

export function listTemplates(params: TemplateListParams = {}, pluginBaseUrl = '') {
  return request<TemplateListResult<DesignTemplate>>('', {
    params: listParams(params),
    pluginBaseUrl,
  });
}

/** Lightweight admin page of templates (no designer state) plus per-status counts. */
export function listTemplateSummaries(params: Omit<TemplateListParams, 'context' | 'fields'> = {}) {
  return request<TemplateListResult<DesignTemplateSummary>>('', {
    params: listParams({ ...params, context: 'admin', fields: 'summary' }),
  });
}

export function listTemplateTags() {
  return request<{ tags: TemplateTag[] }>('/tags');
}

export function bulkTemplates(ids: number[], action: TemplateBulkAction, tag = '') {
  return request<{ changed: number }>('/bulk', {
    method: 'POST',
    body: { ids, action, tag },
  });
}

export function duplicateTemplate(id: number) {
  return request<{ template: DesignTemplate }>(`/${id}/duplicate`, { method: 'POST' });
}

export function listPublicTemplates(params: TemplateListParams = {}, pluginBaseUrl = '') {
  return request<TemplateListResult<PublicDesignTemplate>>('', {
    params: listParams(params),
    pluginBaseUrl,
  });
}

export function getTemplate(id: number, admin = false) {
  return request<{ template: DesignTemplate }>(`/${id}`, {
    params: admin ? { context: 'admin' } : {},
  });
}

export function saveTemplate(template: SaveDesignTemplateInput) {
  const id = Number(template.id || 0);
  return request<{ template: DesignTemplate }>(id > 0 ? `/${id}` : '', {
    method: id > 0 ? 'PUT' : 'POST',
    body: template,
  });
}

export function deleteTemplate(id: number) {
  return request<{ deleted: number }>(`/${id}`, { method: 'DELETE' });
}

export interface TemplateAssignment {
  woo_product_id: number;
  woo_product_title?: string;
  catalog_product_id: number;
  template: DesignTemplate | null;
  quick_personalization?: boolean;
  replace_product_image?: boolean;
  show_loop_personalize_button?: boolean;
  mockup_attachment_id?: number;
  mockup_url?: string;
  mockup_signature?: string;
  stored_mockup_signature?: string;
}

export function getTemplateAssignment(wooProductId: number) {
  return request<TemplateAssignment>('/assignment', {
    params: { woo_product_id: String(wooProductId) },
  });
}

export function assignTemplate(id: number, wooProductId: number) {
  return request<{ woo_product_id: number; template: DesignTemplate }>(`/${id}/assign`, {
    method: 'POST',
    body: { woo_product_id: wooProductId },
  });
}

export function removeTemplateAssignment(wooProductId: number) {
  return request<{ woo_product_id: number; deleted: boolean }>('/assignment', {
    method: 'DELETE',
    params: { woo_product_id: String(wooProductId) },
  });
}

/** Config for a Woo product and its assigned template — the input to the mockup render. */
export function getQuickConfig<T = DesignerTemplateConfig>(wooProductId: number) {
  return request<{ config: T }>('/quick-config', {
    params: { woo_product_id: String(wooProductId) },
  });
}

/** Upload the composite rendered for this product; becomes its WooCommerce image. */
export function saveProductMockup(wooProductId: number, mockupDataUrl: string) {
  return request<{
    woo_product_id: number;
    mockup_attachment_id: number;
    mockup_url: string;
    mockup_signature: string;
    stored_mockup_signature: string;
  }>('/assignment/mockup', {
    method: 'POST',
    body: { woo_product_id: wooProductId, mockup_data_url: mockupDataUrl },
  });
}

const designerAssetPromises = new Map<string, Promise<void>>();

function absoluteAssetUrl(url: string): string {
  return new URL(url, window.location.href).href;
}

function loadDesignerStyle(url: string): Promise<void> {
  const src = absoluteAssetUrl(url);
  const cached = designerAssetPromises.get(src);
  if (cached) return cached;

  const promise = new Promise<void>((resolve, reject) => {
    const existing = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]'))
      .find((node) => node.href === src);
    if (existing) {
      resolve();
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = src;
    link.dataset.veyopTemplateDesignerAsset = 'style';
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => reject(new Error(t('Could not load template designer styles'))), { once: true });
    document.head.appendChild(link);
  });
  designerAssetPromises.set(src, promise);
  return promise;
}

function loadDesignerScript(url: string): Promise<void> {
  const src = absoluteAssetUrl(url);
  const cached = designerAssetPromises.get(src);
  if (cached) return cached;

  const promise = new Promise<void>((resolve, reject) => {
    const existing = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'))
      .find((node) => node.src === src);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.type = 'module';
    script.src = src;
    script.dataset.veyopTemplateDesignerAsset = 'script';

    // A module that throws while evaluating still fires `load` on its element — the
    // failure only reaches `window.onerror`. Without this the add-on never registers
    // and the composite silently renders without its effects. Correlate by filename so
    // the console names the culprit; still resolve, since one broken optional add-on
    // must not block the render.
    const onWindowError = (event: ErrorEvent) => {
      if (event.filename !== src) return;
      console.error('[veyop] Template designer add-on failed to evaluate:', src, event.error ?? event.message);
    };
    window.addEventListener('error', onWindowError);
    const settle = (done: () => void) => {
      // Run after the current task so an evaluation error reported on `window` lands
      // before the listener is torn down.
      setTimeout(() => {
        window.removeEventListener('error', onWindowError);
        done();
      }, 0);
    };

    script.addEventListener('load', () => settle(resolve), { once: true });
    script.addEventListener(
      'error',
      () => settle(() => reject(new Error(t('Could not load template designer add-ons')))),
      { once: true },
    );
    document.head.appendChild(script);
  });
  designerAssetPromises.set(src, promise);
  return promise;
}

export async function loadTemplateDesignerAssets(assets?: DesignerTemplateConfig['designerAssets']): Promise<void> {
  if (!assets) return;
  await Promise.all((assets.css || []).filter(Boolean).map(loadDesignerStyle));
  await Promise.all((assets.js || []).filter(Boolean).map(loadDesignerScript));
}

export function getTemplateDesignerConfig(params: {
  product?: string | number;
  product_id?: string | number;
  parent?: string | number;
  parent_id?: string | number;
  color?: string;
  qty?: string | number;
  template_id?: string | number;
} = {}) {
  return request<{ config: DesignerTemplateConfig }>('/designer-config', {
    params: listParams(params),
  });
}
